import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import type { App } from 'vue'

import type { CurrentUser } from '@/javascript/interfaces'

const bugsnagApiKey = import.meta.env.BUGSNAG_JS_API_KEY

const getCurrentUser = (): Partial<CurrentUser> | undefined => {
	if (typeof window === 'undefined') {
		return undefined
	}

	return window.current_user
}

if (bugsnagApiKey && typeof window !== 'undefined') {
	const currentUser = getCurrentUser()

	Bugsnag.start({
		apiKey: bugsnagApiKey,
		appVersion: import.meta.env.APP_VERSION || 'unknown',
		onError(event) {
			const errorMessage = event.errors[0]?.errorMessage ?? ''
			const stacktrace = event.errors[0]?.stacktrace ?? []

			// "InboundFilters" is a Zendesk-specific identifier that does not exist anywhere
			// in our codebase, so this message is always a Zendesk widget error.
			// Remove this filter once the Zendesk widget is removed.
			if (errorMessage.includes('InboundFilters')) {
				return false
			}

			// Stack frames with file === 'unknown' indicate scripts that are not served by our
			// infrastructure and cannot be traced back to our code (e.g. browser extensions,
			// injected third-party scripts). Suppress these as they are not actionable.
			if (stacktrace.some(frame => frame.file === 'unknown')) {
				return false
			}

			return true
		},
		plugins: [new BugsnagPluginVue()],
		user: {
			email: currentUser?.email,
			id: currentUser?.id?.toString(),
			name: currentUser?.name,
		},
	})
}

/**
 * Attaches Bugsnag to a Vue 3 application
 * @param app - Vue 3 application instance
 */
export default function (app: App) {
	if (!bugsnagApiKey || typeof window === 'undefined') {
		if (import.meta.env.DEV) {
			return
		}

		return
	}

	try {
		const bugsnagVue = Bugsnag.getPlugin('vue')

		if (bugsnagVue && app) {
			app.use(bugsnagVue)
		}
	} catch (error) {
		if (import.meta.env.DEV) {
			console.error('Failed to initialize Bugsnag Vue plugin:', error)
		}
	}
}
